"use client";

import { TrendingUp, TrendingDown, CheckCircle, XCircle, Clock, Users } from 'lucide-react';
import type { AggregatedPropertyReviews, CombinedPerspectives } from '@/lib/data/review-property-aggregation';

interface ReviewAnalyticsProps {
  aggregatedData: AggregatedPropertyReviews;
}

export default function ReviewAnalytics({ aggregatedData }: ReviewAnalyticsProps) {
  const { allReviews, averageRatings, combinedPerspectives } = aggregatedData;

  // Calculate overall sentiment score inline
  const totalPositive = Object.values(combinedPerspectives.prosFrequency).reduce((sum: number, count: number) => sum + count, 0);
  const totalNegative = Object.values(combinedPerspectives.consFrequency).reduce((sum: number, count: number) => sum + count, 0);
  const avgRating = Object.values(averageRatings).reduce((sum: number, rating: number) => sum + rating, 0) / Object.keys(averageRatings).length;
  
  const sentimentScore = totalPositive > 0 || totalNegative > 0 
    ? ((totalPositive / (totalPositive + totalNegative)) * 5) + (avgRating / 2)
    : avgRating;
    
  const overallSentimentScore = {
    score: Math.min(10, Math.max(0, sentimentScore)),
    label: sentimentScore >= 7 ? 'Positive' : sentimentScore >= 5 ? 'Mixed' : 'Negative'
  };

  return (
    <div className="bg-white rounded-lg border p-4 sm:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 gap-2">
        <h2 className="text-lg sm:text-xl font-semibold text-gray-900">Property Analytics Dashboard</h2>
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <Users className="h-4 w-4" />
          {allReviews.length} Review{allReviews.length !== 1 ? 's' : ''} Analyzed
        </div>
      </div>
      
      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-3 sm:p-4 rounded-lg border border-blue-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs sm:text-sm font-medium text-blue-600">Overall Sentiment</p>
              <p className="text-xl sm:text-2xl font-bold text-blue-900">{overallSentimentScore.score.toFixed(1)}/10</p>
              <p className="text-xs text-blue-600 mt-1">{overallSentimentScore.label}</p>
            </div>
            <div className="p-2 bg-blue-100 rounded-full">
              {overallSentimentScore.score >= 7 ? (
                <CheckCircle className="h-5 w-5 sm:h-6 sm:w-6 text-blue-600" />
              ) : overallSentimentScore.score >= 5 ? (
                <Clock className="h-5 w-5 sm:h-6 sm:w-6 text-blue-600" />
              ) : (
                <XCircle className="h-5 w-5 sm:h-6 sm:w-6 text-blue-600" />
              )}
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-3 sm:p-4 rounded-lg border border-green-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs sm:text-sm font-medium text-green-600">Positive Mentions</p>
              <p className="text-xl sm:text-2xl font-bold text-green-900">{Object.keys(combinedPerspectives.prosFrequency).length}</p>
              <p className="text-xs text-green-600 mt-1">Unique Positive Aspects</p>
            </div>
            <div className="p-2 bg-green-100 rounded-full">
              <TrendingUp className="h-5 w-5 sm:h-6 sm:w-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-red-50 to-rose-50 p-3 sm:p-4 rounded-lg border border-red-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs sm:text-sm font-medium text-red-600">Areas of Concern</p>
              <p className="text-xl sm:text-2xl font-bold text-red-900">{Object.keys(combinedPerspectives.consFrequency).length}</p>
              <p className="text-xs text-red-600 mt-1">Unique Negative Aspects</p>
            </div>
            <div className="p-2 bg-red-100 rounded-full">
              <TrendingDown className="h-5 w-5 sm:h-6 sm:w-6 text-red-600" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
} 